import React from 'react';
import { fmtMoney } from '../../utils/orderUtils';
import { decodeHtml } from '../../utils/decodeHtml';
import { nombrePadreLinea, variacionLinea } from '../../utils/nombreLineaPedido';
import '../../pages/OrdersPage.css'; // reusa las clases op-*

/* Helpers compartidos del pedido (F5#4): extraídos VERBATIM de OrderListInspector
   para que el drawer (OrderInspectorDrawer), la lista (OrderListInspector) y sus
   consumidores (Pedidos, Resumen, Caja, los drawers de envío) usen los mismos sin
   dependencia circular. OrderListInspector los RE-EXPORTA → cero cambio para quien
   ya los importa desde ahí. */

export function hasPendingShipping(order) {
  return (
    (order.shipping_lines?.length > 0) &&
    (order.estado_envio !== 'Enviado') &&
    !['cancelled', 'refunded', 'failed'].includes(order.status)
  );
}

export function customerName(order) {
  // Nombre CONGELADO en la venta (billing) gana; si el pedido no lo tiene, el nombre
  // RESUELTO desde la cuenta (nombre_desde_cuenta, lo inyecta el backend para los
  // históricos POS con customer_id y billing vacío); si no hay ninguno, «—».
  const congelado = [order.billing?.first_name, order.billing?.last_name].filter(Boolean).join(' ');
  if (congelado) return congelado;
  const deCuenta = [order.nombre_desde_cuenta?.first_name, order.nombre_desde_cuenta?.last_name].filter(Boolean).join(' ');
  return deCuenta || '—';
}

/* Ítems del pedido con thumbnails — lo comparten la fila expandida y el
   inspector (misma info breve, brief §9). Reusa las clases op-* existentes. */
export function OrderItems({ order }) {
  const items = order.line_items || [];
  if (items.length === 0) return <p className="op-empty">Sin productos.</p>;
  return (
    <div className="op-items">
      {items.map((item, i) => {
        const imgSrc = item.image?.src || null;
        // H9 (23-sep): el nombre es el del PADRE canónico (del espejo, vía backend); NO el
        // `name` crudo, que trae la variación pegada y la duplicaba con la etiqueta de abajo.
        // La etiqueta de variación es la canónica (espejo); si no hay, cae al meta_data de Woo.
        const metaVar = (item.meta_data || [])
          .filter((m) => !m.key?.startsWith('_') && m.display_value && m.display_key)
          .map((m) => decodeHtml(m.display_value))
          .join(' / ');
        const variations = variacionLinea(item) || metaVar;
        return (
          <div key={i} className="op-item">
            {imgSrc
              ? <img src={imgSrc} alt="" className="op-item__thumb" />
              : <div className="op-item__thumb op-item__thumb--empty" />
            }
            <div className="op-item__info">
              <span className="op-item__name">{nombrePadreLinea(item)}</span>
              {variations && <span className="op-item__var">{variations}</span>}
            </div>
            <span className="op-item__qty">×{item.quantity}</span>
            <span className="op-item__price">{fmtMoney(parseFloat(item.total || 0))}</span>
          </div>
        );
      })}
    </div>
  );
}

/* Resumen de montos (descuento/envío/total) — compartido expandida/inspector */
export function OrderTotals({ order }) {
  return (
    <div className="op-order-summary">
      {parseFloat(order.discount_total || 0) > 0 && (
        <div className="op-summary-row op-summary-row--discount">
          <span>Descuento</span>
          <span>-{fmtMoney(parseFloat(order.discount_total))}</span>
        </div>
      )}
      {/* H8 (solo visible, 23-sep): las líneas de cargo/descuento (fee_lines). Un total $0
          por «Descuento (100%)» deja de ser un misterio al ver este renglón negativo. Es el
          mismo dato que ya trae el pedido — sin cambios contables. */}
      {(order.fee_lines || []).map((fee) => {
        const val = parseFloat(fee.total) || 0;
        if (val === 0) return null;
        return (
          <div key={fee.id} className={`op-summary-row${val < 0 ? ' op-summary-row--discount' : ''}`}>
            <span>{fee.name || 'Ajuste'}</span>
            <span>{val < 0 ? '-' : '+'}{fmtMoney(Math.abs(val))}</span>
          </div>
        );
      })}
      {parseFloat(order.shipping_total || 0) > 0 && (
        <div className="op-summary-row">
          <span>Envío</span>
          <span>{fmtMoney(parseFloat(order.shipping_total))}</span>
        </div>
      )}
      <div className="op-summary-row op-summary-row--total">
        <span>Total</span>
        <strong>{fmtMoney(parseFloat(order.total || 0))}</strong>
      </div>
    </div>
  );
}
